import React, { memo, useEffect } from 'react'

import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing_targetingSegments_TargetingSegment,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { useChannel } from '../../hooks/use-abtest.hook'
import { Platform } from '@at/sauron-platform-core'
import { StyledGPTContainer, StyledSearchListing } from '../../patterns/search-listing/search-listing.styles'
import { useCheckPlatform } from '../../hooks/use-check-platform.hook'

interface GPTInSearchProps {
    listing: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing
}

class GPTState {
    private static definedSlots = new Map<string, googletag.Slot>()

    public static isSlotPositionDefined(slotId: string) {
        return GPTState.definedSlots.has(slotId)
    }

    public static addSlot(slotId: string, slot: googletag.Slot) {
        GPTState.definedSlots.set(slotId, slot)
    }

    public static destroySlot(slotId: string) {
        const slotToDestroy = GPTState.definedSlots.get(slotId)
        if (slotToDestroy) {
            window.googletag.destroySlots([slotToDestroy])
            GPTState.definedSlots.delete(slotId)
        }
    }

    public static destroySlots() {
        GPTState.definedSlots.clear()
        window.googletag.destroySlots()
    }
}

export const exportedForTesting = { destroySlots: GPTState.destroySlots }

/**
 * Destroy any previously defined slots and set up the page targeting.
 */
export function initGpt(abTestGroups: string[], searchId: string) {
    if (window) {
        window.googletag.cmd.push(() => {
            GPTState.destroySlots()
            window.googletag
                .pubads()
                .setTargeting('test', `${abTestGroups}`)
                .setTargeting('searchId', searchId)
                .enableSingleRequest()
            window.googletag.pubads().enableLazyLoad({ fetchMarginPercent: 50, mobileScaling: 2 })
        })
    }
}

const MemoisedInSearchListing = memo(function InSearchListing({ posId }: { posId: string }) {
    return (
        <StyledSearchListing data-testid={`gpt-ad-${posId}`}>
            <StyledGPTContainer id={`gpt-ad-${posId}`}></StyledGPTContainer>
        </StyledSearchListing>
    )
})

const GPTInSearch = (props: GPTInSearchProps) => {
    const { listing } = props
    const posId = listing?.posId
    const channel = useChannel()
    const { platform } = useCheckPlatform()
    const gptTargeting = listing?.targetingSegments
    const aoi = listing?.areaOfInterest

    /**
     * Create a slot and set the targeting.
     */
    function createSlot(
        platform: string,
        channel: string,
        posId: string,
        manufacturerCodes?: ReadonlyArray<string>,
        gptTargeting?: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing_targetingSegments_TargetingSegment>
    ) {
        window.googletag.cmd.push(() => {
            if (!GPTState.isSlotPositionDefined(posId)) {
                const slot = window.googletag
                    .defineSlot(
                        `/323304435/${platform === Platform.desktop ? 'web' : 'mobile'}/${channel}/search/listings`,
                        'fluid',
                        `gpt-ad-${posId}`
                    )
                    ?.addService(googletag.pubads())
                    .setTargeting('pos', posId || '')
                    .setTargeting('aoi', `${manufacturerCodes}`)
                    .setCollapseEmptyDiv(true)

                gptTargeting?.forEach((item) => {
                    slot?.setTargeting(`${item?.name}`, `${item?.values}`)
                })
                if (slot) {
                    GPTState.addSlot(posId, slot)
                }
            }
        })
    }

    useEffect(() => {
        if (posId) {
            createSlot(platform, channel, posId, aoi?.manufacturerCodes, gptTargeting)
            window.googletag.cmd.push(() => {
                window.googletag.enableServices()
                window.googletag.display(`gpt-ad-${posId}`)
            })
        }
    }, [aoi?.manufacturerCodes, channel, gptTargeting, platform, posId])

    if (posId) {
        return <MemoisedInSearchListing posId={posId} />
    }

    return null
}

export default GPTInSearch
